import { useModal } from 'modals';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import type { StripeCustomer, StripeProduct } from '@cartwhl/common/types';
import {
  getStripeCustomerIdForBilling,
  isExpiredFreeTrial,
} from '@cartwhl/common/utils/organizationWorkspace';
import Text from '@cartwhl/ui/base/TextNew';
import LockIcon from '@cartwhl/ui/icons/LockIcon';
import Button from '@cartwhl/ui/input/Button';

import { authStore, customerStore, userStore } from '../../stores/global';
import { firstContentSpacing } from '../first-content-spacing';
import { pageFrame } from '../page-frame';
import { publicPageGutters } from '../public-page-gutters';
import {
  type BillingMode,
  type ComparisonSection,
  type PlanAccent,
  type PlanCardContent,
  type PlatformContent,
  type PricingTabContent,
  type PricingTabSlug,
  creditCostRows,
  pricingTabs,
} from './content';
import {
  getCurrentSubscriptionProductId,
  getPricingPlanCTA,
  handlePricingPlanSelection,
  hasExistingSubscriptionForPricing,
  shouldOfferFreeTrialOnPricing,
} from './shared/pricingPlanActions';

const DEFAULT_TAB: PricingTabSlug = 'individual-plans';
const DEFAULT_BILLING: BillingMode = 'yearly';
const CREDITS_ID = 'pricing-credits-explainer';

export default function PricingPage() {
  const { tab } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo, organizationInfo } = useSnapshot(userStore.state);
  const { products, done: productsLoaded } = useSnapshot(
    updatedProductStore.state,
  );
  const requestedAudience = searchParams.get('audience');
  const activeTab = getTab(tab, requestedAudience);
  const billingMode = getBilling(searchParams.get('billing'));
  const activeContent = pricingTabs.find(({ slug }) => slug === activeTab)!;
  const customerId = getStripeCustomerIdForBilling(userInfo, organizationInfo);

  useEffect(() => {
    if ((!tab || tab === activeTab) && requestedAudience === null) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('audience');
    const path =
      activeTab === DEFAULT_TAB ? '/pricing' : `/pricing/${activeTab}`;
    const query = nextSearchParams.size
      ? `?${nextSearchParams.toString()}`
      : '';
    navigate(`${path}${query}${location.hash}`, { replace: true });
  }, [
    activeTab,
    location.hash,
    navigate,
    requestedAudience,
    searchParams,
    tab,
  ]);

  useEffect(() => {
    updatedProductStore.fetchProducts();
  }, []);

  useEffect(() => {
    if (customerId) {
      customerStore.fetchCustomer(customerId);
    }
  }, [customerId]);

  function changeBillingMode(mode: BillingMode) {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('billing', mode);
    setSearchParams(nextSearchParams, { preventScrollReset: true });
  }

  return (
    <div className={`overflow-hidden ${pageFrame}`}>
      <PricingIntro />
      <section className={`border-primary border-b pb-8 lg:pb-12 ${publicPageGutters}`}>
        <div className="mx-auto flex max-w-[1488px] flex-col gap-8 lg:gap-10">
          <PlanIntro />
          <PricingTabs activeTab={activeTab} billingMode={billingMode} />
          {activeContent.cards && (
            <BillingControl
              billingMode={billingMode}
              savings={updatedProductStore.getSavings('individual_pro')}
              onChange={changeBillingMode}
            />
          )}
        </div>
        {activeContent.cards && (
          <PlanCards
            cards={activeContent.cards}
            products={
              products as unknown as Record<string, StripeProduct | null>
            }
            productsLoaded={productsLoaded}
            billingMode={billingMode}
          />
        )}
        {activeContent.platform && (
          <PlatformHero content={activeContent.platform} />
        )}
      </section>
      {activeContent.comparison && activeContent.columns && (
        <Comparison content={activeContent} />
      )}
      {activeContent.platform && (
        <PlatformDetails content={activeContent.platform} />
      )}
      <Credits />
      {!isExpiredFreeTrial(organizationInfo) && (
        <MobileTrialCta activeContent={activeContent} />
      )}
    </div>
  );
}

function PricingIntro() {
  return (
    <section
      className={`pb-8 lg:pb-10 ${firstContentSpacing} ${publicPageGutters}`}
    >
      <div className="mx-auto flex max-w-[1488px] flex-col gap-8 text-center lg:gap-10">
        <Text
          id="pricing-title"
          as="h1"
          token={{ base: '24/medium', lg: '32/medium' }}
          leading="editorial"
          className="text-pretty"
        >
          Pricing plans
        </Text>
        <Text
          as="p"
          token={{ base: '24', lg: '32' }}
          leading="editorial"
          className="text-secondary mx-auto max-w-[1134px] text-pretty"
        >
          Choose the plan that fits how you work—from individual creation to
          team collaboration to production‑scale API workflows.
        </Text>
      </div>
    </section>
  );
}

function PlanIntro() {
  return (
    <Text
      id="pricing-plan-intro"
      as="p"
      token={{ base: '16', lg: '24' }}
      leading={{ base: 'body', lg: 'editorial' }}
      className="mx-auto w-full max-w-[1488px] text-pretty text-center"
    >
      <span className="text-primary cw-green-underline">
        Plans include access to Cartwheel features and a monthly credit
        allotment.
      </span>{' '}
      Credits are used to capture and generate motion. Different workflows
      consume credits based on duration and complexity. As usage grows, you can
      purchase more capacity and unlock higher throughput, collaboration, and
      support.{' '}
      <a href={`#${CREDITS_ID}`} className="text-blue cw-blue-underline">
        Learn how credits work
      </a>
      .
    </Text>
  );
}

interface PricingTabsProps {
  activeTab: PricingTabSlug;
  billingMode: BillingMode;
}

function PricingTabs({ activeTab, billingMode }: PricingTabsProps) {
  const navigate = useNavigate();

  return (
    <div
      className="bg-tertiary grid h-12 grid-cols-3 rounded-xl p-1"
      role="tablist"
      aria-label="Pricing plan category"
    >
      {pricingTabs.map((item) => (
        <button
          key={item.slug}
          type="button"
          role="tab"
          aria-selected={item.slug === activeTab}
          className={`rounded-lg ${item.slug === activeTab ? 'bg-primary' : 'text-secondary'}`}
          onClick={() =>
            navigate(
              pricingLocation(
                item.slug,
                new URLSearchParams({ billing: billingMode }),
              ),
            )
          }
        >
          <Text token={{ base: '16/medium', lg: '20/medium' }}>
            <span className="lg:hidden">{item.shortLabel}</span>
            <span className="hidden lg:inline">{item.label}</span>
          </Text>
        </button>
      ))}
    </div>
  );
}

interface BillingControlProps {
  billingMode: BillingMode;
  savings: number | null;
  onChange: (mode: BillingMode) => void;
}

function BillingControl({
  billingMode,
  savings,
  onChange,
}: BillingControlProps) {
  return (
    <div
      role="radiogroup"
      className="flex flex-wrap items-center gap-5 lg:justify-center lg:gap-[23px]"
    >
      <BillingOption
        checked={billingMode === 'monthly'}
        label="Pay Monthly"
        onClick={() => onChange('monthly')}
      />
      <BillingOption
        checked={billingMode === 'yearly'}
        label={savings ? `Pay Yearly - ${savings}% off` : 'Pay Yearly'}
        onClick={() => onChange('yearly')}
      />
    </div>
  );
}

interface BillingOptionProps {
  checked: boolean;
  label: string;
  onClick: () => void;
}

function BillingOption({ checked, label, onClick }: BillingOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onClick}
      className={`flex items-center gap-2 ${checked ? 'text-primary' : 'text-secondary'}`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${checked ? 'border-primary' : 'border-secondary'}`}
      >
        <span
          className={`h-2.5 w-2.5 rounded-full bg-black ${checked ? '' : 'hidden'}`}
        />
      </span>
      <Text token={{ base: '14/medium', lg: '22/medium' }}>{label}</Text>
    </button>
  );
}

interface PlanCardsProps {
  cards: PlanCardContent[];
  products: Record<string, StripeProduct | null>;
  productsLoaded: boolean;
  billingMode: BillingMode;
}

function PlanCards({
  cards,
  products,
  productsLoaded,
  billingMode,
}: PlanCardsProps) {
  return (
    <div
      id="pricing-cards"
      className={`mx-auto mt-8 grid w-full gap-8 lg:mt-12 ${cards.length === 2 ? 'lg:grid-cols-2 lg:gap-10' : 'lg:grid-cols-3 lg:gap-6'}`}
    >
      {cards.map((card) => (
        <PlanCard
          key={card.productKey}
          card={card}
          product={products[card.productKey]}
          productsLoaded={productsLoaded}
          billingMode={billingMode}
        />
      ))}
    </div>
  );
}

interface PlanCardProps {
  card: PlanCardContent;
  product: StripeProduct | null;
  productsLoaded: boolean;
  billingMode: BillingMode;
}

function PlanCard({
  card,
  product,
  productsLoaded,
  billingMode,
}: PlanCardProps) {
  const navigate = useNavigate();
  const { authStatus } = useSnapshot(authStore.state);
  const { customer } = useSnapshot(customerStore.state);
  const { userInfo, organizationInfo } = useSnapshot(userStore.state);
  const interval = billingMode === 'yearly' ? 'year' : 'month';
  const price = updatedProductStore.getPriceByProductAndMode(
    product?.id,
    interval,
  );
  const customerId = getStripeCustomerIdForBilling(userInfo, organizationInfo);
  const hasExistingSubscription = hasExistingSubscriptionForPricing(
    customerStore.getSubscriberPremiumStatus(customer as StripeCustomer),
    organizationInfo,
  );
  const currentProductId = getCurrentSubscriptionProductId(
    customer as StripeCustomer,
    organizationInfo,
    userInfo,
  );
  const cta = getPricingPlanCTA(
    authStatus,
    hasExistingSubscription,
    currentProductId,
    product?.id,
    card.title,
    shouldOfferFreeTrialOnPricing(organizationInfo),
  );

  if (!price) {
    return productsLoaded ? (
      <PlanCardUnavailable card={card} />
    ) : (
      <PlanCardSkeleton accent={card.accent} />
    );
  }

  const displayPrice = formatPrice(price.unit_amount, interval);

  async function selectPlan() {
    if (!product?.id) {
      return;
    }

    await handlePricingPlanSelection({
      authStatus,
      hasExistingSubscription,
      currentProductId,
      productId: product.id,
      stripeCustomerId: customerId,
      price: price!,
      navigate,
    });
  }

  return (
    <article
      className={`bg-secondary flex min-h-[360px] flex-col rounded-2xl px-6 py-8 sm:px-8 lg:min-h-[487px] ${card.accent === 'blue' ? 'lg:p-10' : 'lg:px-6 lg:py-10'}`}
    >
      <div className="flex flex-col gap-6 lg:gap-10">
        <Text
          as="h2"
          token={{ base: '20/medium', lg: '32' }}
          leading={{ base: 'compact', lg: 'editorial' }}
          className="flex items-center gap-3"
        >
          {card.accent !== 'neutral' && <PlanAccentDot accent={card.accent} />}
          {card.title}
        </Text>
        <Text
          as="p"
          token={{ base: '16', lg: '24' }}
          leading="editorial"
          className="text-secondary"
        >
          {card.description}
        </Text>
      </div>
      <div className="mt-auto flex flex-col gap-6 pt-8">
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-2 whitespace-nowrap">
            <Text
              token={{ base: 'display/40/bold', lg: 'display/64/bold' }}
              className="!font-sans"
            >
              ${displayPrice}
            </Text>
            <Text token="20" leading="compact">
              {card.periodLabel[billingMode]}
            </Text>
          </div>
          {billingMode === 'yearly' && (
            <Text token="16" className="text-secondary">
              ${formatPrice(price.unit_amount, 'month')} billed yearly
            </Text>
          )}
          <Text token="16" className="text-secondary">
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById(CREDITS_ID)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
              className="text-blue font-inherit cursor-pointer bg-transparent p-0 underline"
            >
              {card.credits}
            </button>{' '}
            {card.creditsPeriod}
          </Text>
        </div>
        <Button
          type="button"
          variant="primary"
          disabled={cta === 'Your current plan'}
          className={`h-10 w-full ${buttonClass(card.accent)}`}
          onClick={selectPlan}
        >
          {cta}
        </Button>
      </div>
    </article>
  );
}

interface PlanAccentDotProps {
  accent: PlanAccent;
}

function PlanAccentDot({ accent }: PlanAccentDotProps) {
  return (
    <span
      className={`h-4 w-4 rounded-full lg:h-6 lg:w-6 ${accent === 'green' ? 'bg-green-50' : 'bg-blue'}`}
    />
  );
}

interface PlanCardSkeletonProps {
  accent: PlanAccent;
}

function PlanCardSkeleton({ accent }: PlanCardSkeletonProps) {
  return (
    <article
      className={`bg-secondary flex min-h-[360px] animate-pulse flex-col rounded-2xl p-6 sm:p-8 lg:min-h-[487px] ${accent === 'blue' ? 'lg:p-10' : 'lg:px-6 lg:py-10'}`}
    >
      <div className="bg-tertiary h-8 w-2/3 rounded" />
      <div className="bg-tertiary mt-6 h-20 rounded" />
      <div className="bg-tertiary mt-auto h-16 w-1/2 rounded" />
      <div className="bg-tertiary mt-6 h-10 rounded" />
    </article>
  );
}

function PlanCardUnavailable({ card }: { card: PlanCardContent }) {
  return (
    <article className="bg-secondary flex min-h-[360px] flex-col rounded-2xl p-6 sm:p-8 lg:min-h-[487px]">
      <Text as="h2" token={{ base: '20/medium', lg: '32' }}>
        {card.title}
      </Text>
      <Text as="p" token="16" className="text-secondary mt-6">
        This plan's pricing is unavailable. Please try again.
      </Text>
      <Button
        type="button"
        variant="primary"
        className="mt-auto h-10 w-full"
        onClick={() => void updatedProductStore.retryProducts()}
      >
        Retry pricing
      </Button>
    </article>
  );
}

interface ComparisonProps {
  content: PricingTabContent;
}

function Comparison({ content }: ComparisonProps) {
  if (content.slug === 'team-plans') {
    return <TeamPlanDetails content={content} />;
  }

  const columns = content.columns!;

  return (
    <section className={`border-primary border-t py-10 lg:py-14 ${publicPageGutters}`}>
      <div className="flex flex-col gap-10 lg:gap-20">
        <Text
          as="h2"
          token={{ base: '24/medium', lg: '32' }}
          leading="editorial"
        >
          {content.comparisonTitle}
        </Text>
        <div
          className={`bg-tertiary grid min-h-16 items-center gap-4 px-4 ${columns.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} lg:bg-transparent lg:px-0`}
        >
          {columns.map((column) => (
            <div
              key={column.label}
              className="flex items-center gap-2 lg:gap-3"
            >
              {column.accent !== 'neutral' && (
                <PlanAccentDot accent={column.accent} />
              )}
              <Text token={{ base: '16/bold', lg: '32' }} leading="editorial">
                {column.label}
              </Text>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-10">
          {content.comparison!.map((section) => (
            <ComparisonSectionView
              key={section.label}
              section={section}
              columns={columns.length}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface TeamPlanDetailsProps {
  content: PricingTabContent;
}

function TeamPlanDetails({ content }: TeamPlanDetailsProps) {
  const columns = content.columns!;
  const sections = content.comparison!;
  const [apps, collaboration, api, support] = sections;

  return (
    <>
      <section className={`border-primary hidden border-b py-14 lg:block ${publicPageGutters}`}>
        <div className="flex flex-col gap-12">
          <TeamDesktopHeaders columns={columns} />
          <TeamDivider />
          <TeamDesktopCategory section={apps} />
          <TeamDivider />
          <TeamDesktopCategory section={collaboration} />
          <TeamDivider />
          <TeamDesktopApi section={api} />
          <TeamDivider />
          <TeamDesktopCategory section={support} />
        </div>
      </section>
      <TeamMobileDetails
        columns={columns}
        sections={[apps, collaboration, api, support]}
      />
    </>
  );
}

interface TeamDesktopHeadersProps {
  columns: NonNullable<PricingTabContent['columns']>;
}

function TeamDesktopHeaders({ columns }: TeamDesktopHeadersProps) {
  return (
    <div className="grid grid-cols-3 gap-6">
      {columns.map((column) => (
        <div key={column.label} className="flex items-center gap-2 px-8">
          {column.accent !== 'neutral' && (
            <PlanAccentDot accent={column.accent} />
          )}
          <Text token="32" leading="editorial" className="tracking-[-0.64px]">
            {column.label}
          </Text>
        </div>
      ))}
    </div>
  );
}

function TeamDivider() {
  return <div className="border-primary h-[17px] border-t" />;
}

interface TeamDesktopCategoryProps {
  section: ComparisonSection;
}

function TeamDesktopCategory({ section }: TeamDesktopCategoryProps) {
  return (
    <div className="flex flex-col gap-12">
      <Text
        as="h3"
        token="32"
        leading="editorial"
        className="px-8 tracking-[-0.64px]"
      >
        {section.label}
      </Text>
      <div className="grid grid-cols-3 gap-6">
        {section.values.map((values, index) => (
          <div
            key={`${section.label}-${index}`}
            className="flex flex-col gap-8 px-8"
          >
            {values.map((value) => (
              <Text
                key={value.label}
                as="p"
                token="24"
                leading="editorial"
                className="text-secondary"
              >
                {value.label}
              </Text>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

interface TeamDesktopApiProps {
  section: ComparisonSection;
}

function TeamDesktopApi({ section }: TeamDesktopApiProps) {
  return (
    <section className="flex flex-col gap-6 rounded-3xl border border-[#c4cf2d] bg-[rgba(239,255,0,0.08)] pb-16 pl-8 pr-6 pt-6">
      <Text
        as="h3"
        token="32"
        leading="editorial"
        className="tracking-[-0.64px]"
      >
        {section.label}
      </Text>
      <div className="grid grid-cols-3 gap-12">
        {section.values.map((values, columnIndex) => (
          <div
            key={`${section.label}-${columnIndex}`}
            className="flex flex-col gap-8"
          >
            {values.map((value) => (
              <div key={value.label} className="flex items-start gap-2">
                {value.locked && <LockIcon className="mt-1 h-5 w-5 shrink-0" />}
                <Text
                  as="p"
                  token="24"
                  leading="compactEditorial"
                  className={
                    value.desktopLineBreak ? 'whitespace-pre-line' : ''
                  }
                >
                  {value.desktopLineBreak ?? value.label}
                </Text>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

interface TeamMobileDetailsProps {
  columns: NonNullable<PricingTabContent['columns']>;
  sections: ComparisonSection[];
}

function TeamMobileDetails({ columns, sections }: TeamMobileDetailsProps) {
  const [apps, collaboration, api, support] = sections;

  return (
    <section className="flex flex-col gap-6 pb-10 lg:hidden">
      <Text
        as="h2"
        token="24/medium"
        leading="editorial"
        className="px-4 pb-5 pt-8"
      >
        Team plan details
      </Text>
      <div className="bg-secondary grid h-16 grid-cols-3 gap-4 px-8">
        {columns.map((column) => (
          <div key={column.label} className="flex items-center gap-2">
            {column.accent !== 'neutral' && (
              <PlanAccentDot accent={column.accent} />
            )}
            <Text token="16/bold" leading="body">
              {column.label}
            </Text>
          </div>
        ))}
      </div>
      <div className="mx-auto flex w-[calc(100%-32px)] max-w-[329px] flex-col">
        <TeamMobileCategory section={apps} />
        <TeamMobileCategory section={collaboration} bordered />
        <TeamMobileApi section={api} />
        <TeamMobileCategory section={support} bordered />
      </div>
    </section>
  );
}

interface TeamMobileCategoryProps {
  section: ComparisonSection;
  bordered?: boolean;
}

function TeamMobileCategory({
  section,
  bordered = false,
}: TeamMobileCategoryProps) {
  return (
    <section
      className={`flex flex-col gap-6 pb-8 pt-5 ${bordered ? 'border-primary border-t' : ''}`}
    >
      <Text as="h3" token="16" leading="body" className="text-secondary">
        {section.label}
      </Text>
      <div className="grid grid-cols-3 gap-4">
        {section.values.map((values, columnIndex) => (
          <div
            key={`${section.label}-${columnIndex}`}
            className="flex flex-col gap-6"
          >
            {values.map((value) => (
              <Text
                key={value.label}
                as="p"
                token="16"
                leading="body"
                className="break-words"
              >
                {value.label}
              </Text>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

interface TeamMobileApiProps {
  section: ComparisonSection;
}

function TeamMobileApi({ section }: TeamMobileApiProps) {
  return (
    <section className="-mx-4 mb-0 flex w-[calc(100%+32px)] flex-col gap-6 rounded-2xl border border-[#c4cf2d] bg-[rgba(239,255,0,0.08)] px-4 pb-8 pt-5">
      <Text as="h3" token="16/medium" leading="body">
        {section.label}
      </Text>
      <div className="grid grid-cols-3 gap-4">
        {section.values.map((values, columnIndex) => (
          <div
            key={`${section.label}-${columnIndex}`}
            className="flex flex-col gap-6"
          >
            {values.map((value) => (
              <div
                key={value.label}
                className={`flex items-start gap-1 ${value.muted ? 'text-secondary' : ''}`}
              >
                {value.locked && (
                  <LockIcon className="mt-0.5 h-3 w-3 shrink-0" />
                )}
                <Text as="p" token="16" leading="body" className="break-words">
                  {value.label}
                </Text>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

interface ComparisonSectionViewProps {
  section: ComparisonSection;
  columns: number;
}

function ComparisonSectionView({
  section,
  columns,
}: ComparisonSectionViewProps) {
  const gridClassName =
    columns === 1
      ? 'grid-cols-1'
      : columns === 2
        ? 'grid-cols-2'
        : 'grid-cols-3';

  return (
    <section
      className={
        section.highlighted
          ? '-mx-4 rounded-2xl border border-yellow-50 bg-yellow-50/10 px-4 py-6 sm:mx-0 sm:px-8 lg:p-8'
          : 'border-primary border-t pt-6 lg:pt-8'
      }
    >
      <Text
        as="h3"
        token={{ base: '16/medium', lg: '32' }}
        leading="editorial"
        className="text-secondary lg:text-primary mb-6 lg:mb-8"
      >
        {section.label}
      </Text>
      <div className={`grid gap-4 lg:gap-6 ${gridClassName}`}>
        {section.values.map((values, columnIndex) => (
          <div
            key={`${section.label}-${columnIndex}`}
            className="flex min-w-0 flex-col gap-5 lg:gap-6"
          >
            {values.map((value) => (
              <div
                key={`${section.label}-${columnIndex}-${value.label}`}
                className={`flex items-start gap-2 ${value.muted ? 'text-secondary' : ''}`}
              >
                {value.locked && (
                  <LockIcon className="mt-0.5 h-4 w-4 shrink-0 lg:h-6 lg:w-6" />
                )}
                <Text
                  as="p"
                  token={{ base: '16', lg: '24' }}
                  leading="editorial"
                  className="break-words"
                >
                  {value.label}
                </Text>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

interface PlatformHeroProps {
  content: PlatformContent;
}

function PlatformHero({ content }: PlatformHeroProps) {
  const { open } = useModal();

  return (
    <article className="bg-secondary mx-auto mt-8 flex w-full flex-col gap-10 rounded-2xl border border-purple-50 bg-purple-50/10 p-6  sm:mt-10 sm:p-8 lg:p-10">
      <div className="flex max-w-[1040px] flex-col gap-8">
        <Text as="h2" token={{ base: '24', lg: '32' }} leading="editorial">
          <span className="text-purple-60">{content.title}</span>
        </Text>
        <Text
          as="p"
          token={{ base: '16', lg: '24' }}
          leading="editorial"
          className="text-secondary"
        >
          {content.description}
        </Text>
        <Button
          type="button"
          variant="primary"
          className="w-full bg-[#5900F5] hover:bg-[#4600C2] lg:w-fit"
          onClick={() =>
            open('ENTERPRISE_CONTACT', { reason: 'enterprise_plan_or_project' })
          }
        >
          Contact us
        </Button>
      </div>
      <div className="border-primary grid gap-8 border-t pt-8 lg:grid-cols-2 lg:gap-14">
        {content.features.map((feature) => (
          <div key={feature.title} className="flex flex-col gap-4">
            <Text
              as="h3"
              token={{ base: '20/medium', lg: '24' }}
              leading="editorial"
            >
              {feature.title}
            </Text>
            <Text
              as="p"
              token={{ base: '16', lg: '24' }}
              leading="editorial"
              className="text-secondary"
            >
              {feature.body}
            </Text>
          </div>
        ))}
      </div>
    </article>
  );
}

interface PlatformDetailsProps {
  content: PlatformContent;
}

function PlatformDetails({ content }: PlatformDetailsProps) {
  return (
    <section className={`border-primary border-t py-20 ${publicPageGutters}`}>
      <div className="flex flex-col gap-10 lg:gap-20">
        <div className="flex max-w-[1134px] flex-col gap-8">
          <Text
            as="h2"
            token={{ base: '24/medium', lg: '32' }}
            leading="editorial"
          >
            Enterprise plan details
          </Text>
          <Text
            as="p"
            token={{ base: '24', lg: '32' }}
            leading="editorial"
            className="text-secondary"
          >
            The Enterprise plan includes everything in the Team Scale plan at a
            base level. Concurrent jobs and batch sizes are custom designed to
            your unique use case. SSO, custom support, and isolated data
            environments are also available at the enterprise level.
          </Text>
        </div>
        <div className="flex flex-col gap-10">
          {content.details.map((section) => (
            <ComparisonSectionView
              key={section.label}
              section={section}
              columns={1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Credits() {
  return (
    <section
      id={CREDITS_ID}
      className={`border-primary scroll-mt-24 border-y py-10 lg:scroll-mt-32 lg:pb-40 lg:pt-20 ${publicPageGutters}`}
    >
      <div className="flex flex-col gap-10">
        <Text
          as="h2"
          token={{ base: '24/medium', lg: '32/medium' }}
          leading="editorial"
        >
          How credits work
        </Text>
        <div className="grid gap-10 xl:grid-cols-2 xl:gap-24 2xl:gap-48">
          <CreditsExplanation />
          <CreditCosts />
        </div>
      </div>
    </section>
  );
}

function CreditsExplanation() {
  return (
    <div className="flex flex-col gap-10 lg:gap-14">
      <Text
        as="p"
        token={{ base: '24', lg: '32' }}
        leading="editorial"
        className="text-secondary"
      >
        Credits are used to measure usage across Cartwheel
      </Text>
      <Text
        as="div"
        token={{ base: '16', lg: '24' }}
        leading={{ base: 'body', lg: 'editorial' }}
        className="cw-text-box-none text-secondary"
      >
        <ul className="list-disc space-y-0 pl-5 lg:pl-9">
          <li>
            Swing Sketch uses{' '}
            <span className="text-primary cw-green-underline">
              1 credit per second of animation
            </span>
            .
          </li>
          <li>
            Swing Choreo, multi-character capture, and mascot generation use
            more credits based on complexity
          </li>
          <li>
            <span className="text-primary cw-green-underline">
              Every plan includes a fixed number of credits per month
            </span>{' '}
            which restart each month. You can always purchase more as needed in
            your Plan &amp; Billing tab. A la carte purchases will roll over to
            the next month.
          </li>
          <li>
            <span className="text-primary cw-green-underline">
              Additional credits can be purchased in Plan &amp; Billing
            </span>
          </li>
          <li>
            <span className="text-primary cw-green-underline">
              Automatic credit top-ups are optional and off by default
            </span>
          </li>
        </ul>
      </Text>
    </div>
  );
}

function CreditCosts() {
  return (
    <div className="flex flex-col gap-10">
      <Text
        as="h3"
        token={{ base: '24', lg: '32' }}
        leading="editorial"
        className="text-secondary"
      >
        <span className="lg:hidden">Credit costs</span>
        <span className="hidden lg:inline">Credit Costs</span>
      </Text>
      <div className="border-primary rounded-lg border py-6 lg:hidden">
        {creditCostRows.map((row, index) => (
          <CreditCostMobileRow
            key={row.label}
            row={row}
            isLast={index === creditCostRows.length - 1}
          />
        ))}
      </div>
      <div className="border-primary hidden rounded-3xl border py-8 lg:block">
        {creditCostRows.map((row, index) => (
          <CreditCostDesktopRow
            key={row.label}
            row={row}
            isLast={index === creditCostRows.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

interface CreditCostRowProps {
  row: (typeof creditCostRows)[number];
  isLast: boolean;
}

function CreditCostDesktopRow({ row, isLast }: CreditCostRowProps) {
  return (
    <div>
      <div className="flex items-start gap-4 px-8">
        <div
          className={`flex flex-col gap-1 ${row.subrows ? 'w-[34%] shrink-0' : 'w-1/2'}`}
        >
          <Text token="24/medium" leading="title">
            {row.label}
          </Text>
          {row.subtitle && (
            <Text token="14" leading="compact" className="text-secondary">
              {row.subtitle}
            </Text>
          )}
        </div>
        {row.value && (
          <Text
            token="20"
            leading="compact"
            className="text-secondary w-1/2 text-right"
          >
            {row.value}
          </Text>
        )}
        {row.subrows && <CreditCostDesktopSubrows subrows={row.subrows} />}
      </div>
      {!isLast && (
        <div className="px-8 py-7">
          <div className="border-primary border-b" />
        </div>
      )}
    </div>
  );
}

function CreditCostMobileRow({ row, isLast }: CreditCostRowProps) {
  return (
    <div>
      <div className="flex flex-col gap-6 px-6">
        <div className="flex flex-col gap-1">
          <Text token="16" leading="body">
            {row.label}
          </Text>
          {row.subtitle && (
            <Text token="14" leading="compact" className="text-secondary">
              {row.subtitle}
            </Text>
          )}
        </div>
        {row.value && (
          <Text token="14" leading="compact" className="text-secondary">
            {row.value}
          </Text>
        )}
        {row.subrows && <CreditCostMobileSubrows subrows={row.subrows} />}
      </div>
      {!isLast && (
        <div className="px-6 py-6">
          <div className="border-primary border-b" />
        </div>
      )}
    </div>
  );
}

interface CreditCostDesktopSubrowsProps {
  subrows: NonNullable<(typeof creditCostRows)[number]['subrows']>;
}

function CreditCostDesktopSubrows({ subrows }: CreditCostDesktopSubrowsProps) {
  return (
    <div className="grid flex-1 grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-x-4 gap-y-1">
      {subrows.map((subrow) => (
        <div key={subrow.label} className="contents">
          <Text token="20" leading="compact" className="text-secondary min-w-0">
            {subrow.label}
          </Text>
          <Text
            token="20"
            leading="compact"
            className="text-secondary min-w-0 text-right"
          >
            {subrow.value}
          </Text>
        </div>
      ))}
    </div>
  );
}

interface CreditCostMobileSubrowsProps {
  subrows: NonNullable<(typeof creditCostRows)[number]['subrows']>;
}

function CreditCostMobileSubrows({ subrows }: CreditCostMobileSubrowsProps) {
  return (
    <div className="flex flex-col gap-6">
      {subrows.map((subrow) => (
        <div key={subrow.label} className="flex flex-col gap-1">
          <Text token="14" leading="compact" className="text-secondary">
            {subrow.label}
          </Text>
          <Text token="14" leading="compact" className="text-secondary">
            {subrow.value}
          </Text>
        </div>
      ))}
    </div>
  );
}

interface MobileTrialCtaProps {
  activeContent: PricingTabContent;
}

function MobileTrialCta({ activeContent }: MobileTrialCtaProps) {
  if (!activeContent.cards) {
    return null;
  }

  return (
    <section className={`border-primary border-b py-20 lg:hidden ${publicPageGutters}`}>
      <div className="flex flex-col gap-8">
        <Text as="h2" token="24/medium" leading="editorial">
          Not sure yet?
        </Text>
        <Text as="p" token="24" leading="editorial" className="text-secondary">
          We offer a 7-day free trial so you can evaluate whether Cartwheel
          meets your creation needs and accelerates your production workflows.
        </Text>
        <Button
          type="button"
          variant="primary"
          className="bg-green-35 text-primary h-10 w-full hover:bg-green-50"
          onClick={() =>
            document
              .getElementById('pricing-cards')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        >
          Start free trial
        </Button>
      </div>
    </section>
  );
}

function buttonClass(accent: PlanAccent) {
  if (accent === 'green') {
    return 'bg-green-35 text-primary hover:bg-green-50';
  }
  if (accent === 'blue') {
    return 'bg-blue text-white';
  }
  return 'bg-black text-white hover:bg-gray-90';
}

function getTab(
  tab: string | undefined,
  audience: string | null,
): PricingTabSlug {
  if (pricingTabs.some(({ slug }) => slug === tab)) {
    return tab as PricingTabSlug;
  }
  if (audience === 'teams') return 'team-plans';
  if (audience === 'enterprise') return 'enterprise-plans';
  return DEFAULT_TAB;
}

function getBilling(value: string | null): BillingMode {
  return value === 'yearly' || value === 'monthly' ? value : DEFAULT_BILLING;
}

function pricingLocation(tab: PricingTabSlug, params: URLSearchParams) {
  return `/pricing/${tab}?${params.toString()}`;
}

function formatPrice(unitAmount: number, interval: 'year' | 'month') {
  const amount = updatedProductStore.formatMoney(
    interval === 'year' ? unitAmount / 12 : unitAmount,
  );

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(interval === 'year' ? Math.ceil(amount) : amount);
}
